import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getInstructionById, createInstruction, updateInstruction } from '../../api/pmsApi';

// 날짜 선택기
import CommonDatePicker from "../../components/DatePicker/CommonDatePicker.js";

// 에디터
import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../components/editor/EditorToolbar.js';
import tiptapExtensions from '../../components/editor/tiptapExtensions.js';
import useEditorWithDragDrop from '../../components/editor/useEditorWithDragDrop.js';

// 조직도 (팀장 선택)
import OrgTreeSelect from '../../components/OrgSelect/OrgTreeSelect.js';

function InstructionForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [dueDate, setDueDate] = useState(new Date());

  // 선택된 수신자 배열
  const [leaders, setLeaders] = useState([]);

  // 조직도 팝업 제어
  const [showOrgPopup, setShowOrgPopup] = useState(false);

  const editor = useEditorWithDragDrop(tiptapExtensions);

  useEffect(() => {
    if (isEdit && id && token && editor) {
      getInstructionById(id, token).then((res) => {
        if (res.ok) {
          const data = res.data;
          setTitle(data.title || '');
          setSubject(data.subject || '');
          if (data.due_date) setDueDate(new Date(data.due_date));
          if (data.content) editor.commands.setContent(data.content);

          if (data.leaders) {
            setLeaders(data.leaders.map(l => ({
              target_id: l.user?.userid || String(l.leader_id),
              target_type: 'user',
              disp_target_name: l.user?.name
                ? `${l.user.name}${l.user.org?.position ? ' / ' + l.user.org.position : ''}${l.user.org?.dept_name ? '(' + l.user.org.dept_name + ')' : ''}`
                : `ID: ${l.leader_id}`,
              name: l.user?.name
            })));
          }
        }
      }).catch(err => {
        console.error('요청 정보 로드 실패:', err);
        alert('데이터를 불러오는데 실패했습니다.');
        navigate(-1);
      });
    }
  }, [id, isEdit, token, editor, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return alert('제목을 입력해주세요.');
    if (!dueDate) return alert('생성 기한을 선택해주세요.');
    if (leaders.length === 0) return alert('요청을 수신할 담당자를 최소 1명 이상 지정해주세요.');

    const formatDueDate = `${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, '0')}-${String(dueDate.getDate()).padStart(2, '0')}`;

    const data = {
      title,
      subject,
      content: editor ? editor.getHTML() : '',
      due_date: formatDueDate,
      leaderIds: leaders.map(l => l.target_id)
    };

    try {
      if (isEdit) {
        await updateInstruction(id, data, token);
        alert('성공적으로 수정(재요청)되었습니다.');
      } else {
        await createInstruction(data, token);
        alert('성공적으로 요청되었습니다.');
      }
      navigate(-1);
    } catch (err) {
      console.error('저장 실패:', err);
      alert('저장에 실패했습니다.');
    }
  };

  const removeLeader = (target_id) => {
    setLeaders(prev => prev.filter(l => l.target_id !== target_id));
  };

  return (
    <div className="container-fluid p-4">
      {/* 상단 헤더 */}
      <div className="d-flex justify-content-between align-items-center mb-4 p-3 rounded shadow-sm border-0"
        style={{ background: 'linear-gradient(90deg, #607d8b 0%, #cfd8dc 100%)', color: '#fff' }}>
        <h4 className="mb-0 fw-bold">
          <i className="fas fa-edit me-2"></i>{isEdit ? '프로젝트 요청 수정' : '새로운 프로젝트 요청 작성'}
        </h4>
        <button className="btn btn-light fw-bold text-dark btn-sm" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left me-1"></i>뒤로가기
        </button>
      </div>

      <style>{`
        .light-placeholder::placeholder {
          color: #adb5bd !important;
          font-weight: 400;
        }
        /* PMS 달력 전용 스타일 격리 */
        .pms-datepicker-container .react-datepicker-wrapper {
          width: 100% !important;
        }
      `}</style>
      <form onSubmit={handleSubmit}>
        <div className="row g-4">

          {/* 📝 좌측: 지시 내용 */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm rounded-4 mb-4">
              <div className="card-header bg-white border-bottom-0 pt-4 pb-0">
                <h5 className="fw-bold text-dark"><i className="fas fa-clipboard-list me-2 text-primary"></i>프로젝트 요청</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-bold"><span className="text-primary fw-bold me-2 fs-5">T</span>요청 제목<span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control light-placeholder"
                    style={{ backgroundColor: '#ffffff', color: '#212529' }}
                    placeholder="이 요청의 핵심 목적을 입력하세요."
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold"><span className="text-primary fw-bold me-2 fs-5">H</span>프로젝트<span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control light-placeholder"
                    style={{ backgroundColor: '#ffffff', color: '#212529' }}
                    placeholder="프로젝트명을 입력하세요."
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-2">
                  <label className="form-label fw-bold"><i className="fas fa-align-left text-primary fs-5 me-2"></i>상세 요청 내용</label>
                  <div className="border rounded-3 bg-light">
                    {editor && <EditorToolbar editor={editor} />}
                    <div className="bg-white" style={{ minHeight: '300px' }}>
                      <EditorContent editor={editor} className="editor-content p-3" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ⚙️ 우측: 지시 설정 */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm rounded-4 mb-4">
              <div className="card-header bg-white border-bottom-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
                <h5 className="fw-bold text-dark"><i className="fas fa-users-cog me-2 text-success"></i>요청 설정</h5>
              </div>
              <div className="card-body">

                {/* 작성자 (Readonly) */}
                <div className="mb-4">
                  <label className="form-label fw-bold"><i className="fas fa-user-edit text-primary fs-5 me-2"></i>작성자(요청자)</label>
                  <div className="d-flex align-items-center gap-2 ms-1">
                    <i className="fas fa-user text-secondary"></i>
                    <span className="text-dark">
                      {user?.name}
                      {(user?.position || user?.dept_name) && (
                        <span className="ms-1">
                          / {user?.position}{user?.dept_name ? `(${user?.dept_name})` : ''}
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                <hr className="text-muted opacity-25" />

                {/* 생성 기한 */}
                <div className="mb-4">
                  <label className="form-label fw-bold"><i className="fas fa-calendar-alt text-primary fs-5 me-2"></i>프로젝트 생성 기한 <span className="text-danger">*</span></label>
                  <div className="d-block">
                    <div className="d-inline-block pms-datepicker-container">
                      <CommonDatePicker
                        value={dueDate}
                        onChange={(date) => setDueDate(date)}
                        placeholder="마감일 선택"
                        showTime={false}
                      />
                    </div>
                  </div>
                  <div className="form-text mt-1 text-muted">
                    <i className="fas fa-info-circle me-1"></i>선택된 날짜까지 책임자(PM)가 프로젝트를 등록해야 합니다.
                  </div>
                </div>

                <hr className="text-muted opacity-25" />

                {/* 수신자 설정 */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-bold mb-0"><i className="fas fa-user text-primary fs-5 me-2"></i>책임자(PM) <span className="text-danger">*</span></label>
                    <div className="d-flex gap-2">
                      {leaders.length > 0 && (
                        <button type="button" className="btn btn-sm btn-outline-danger rounded-pill px-3" onClick={() => setLeaders([])}>
                          <i className="fas fa-trash-alt me-1"></i>전체삭제
                        </button>
                      )}
                      <button type="button" className="btn btn-sm btn-outline-primary rounded-pill px-3" onClick={() => setShowOrgPopup(true)}>
                        <i className="fas fa-plus me-1"></i>조직도 선택
                      </button>
                    </div>
                  </div>

                  <div className="p-3 bg-light rounded-3 border border-dashed" style={{ minHeight: '100px', maxHeight: '225px', overflowY: 'auto' }}>
                    {leaders.length === 0 ? (
                      <div className="text-center text-muted py-3">
                        <i className="fas fa-user-plus mb-2 fs-4 opacity-50"></i>
                        <p className="mb-0">조직도에서 대상자를 선택해주세요.</p>
                      </div>
                    ) : (
                      <div className="d-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                        {leaders.map(l => (
                          <span key={l.target_id} className="badge bg-white text-dark border shadow-sm px-3 py-2 d-flex align-items-center justify-content-between"
                            style={{ fontSize: '0.75rem', fontWeight: '500', minWidth: 0 }}
                            title={l.disp_target_name}>
                            <span className="d-flex align-items-center" style={{ minWidth: 0, flex: 1 }}>
                              <i className="fas fa-user-tie text-primary me-2 flex-shrink-0"></i>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {l.disp_target_name}
                              </span>
                            </span>
                            <button type="button" className="btn-close btn-close-sm ms-2 flex-shrink-0"
                              style={{ fontSize: '0.65rem' }} onClick={() => removeLeader(l.target_id)}></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <hr className="text-muted opacity-25 mt-3" />

                {/* 저장 / 취소 버튼 */}
                <div className="d-flex justify-content-end gap-2 pb-2">
                  <button type="submit" className="btn btn-primary px-2 fw-bold rounded-3 shadow-sm">
                    <i className="fas fa-save me-1"></i>{id ? '수정 완료' : '요청 등록'}
                  </button>
                  <button type="button" className="btn btn-secondary px-2 fw-bold text-white rounded-3 shadow-sm" onClick={() => navigate(-1)}>
                    <i className="fas fa-times me-1"></i>취소
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </form>

      {/* 조직도 모달 */}
      {showOrgPopup && (
        <OrgTreeSelect
          onConfirm={(selected) => {
            setLeaders(selected);
            setShowOrgPopup(false);
          }}
          onCancel={() => setShowOrgPopup(false)}
          selectionMode="multiple"
          targetType="user"
          initialSelected={leaders}
        />
      )}
    </div>
  );
}

export default InstructionForm;
